/**
 * The password KDF. Shared verbatim by the browser and the Worker — if these two disagree
 * by a single byte, nobody can log in. ADR-0008.
 *
 * The expensive derivation runs in the BROWSER, because Workers Free allows 10 ms of CPU
 * per request and 600,000 PBKDF2 iterations costs 300–600 ms. See the ADR for the threat
 * model; the short version is that the work factor still protects the password after a
 * database leak, because the stored value commits to authKey which commits to the password
 * through all 600,000 iterations.
 */

/**
 * Bump when either iteration count or the salt derivation changes. Stored per user, so old
 * records stay verifiable and can be re-derived on the next successful sign-in.
 */
export const AUTH_KDF_VERSION = 1;

/** OWASP guidance for PBKDF2-HMAC-SHA256. Paid once per sign-in, in the browser. */
export const CLIENT_ITERATIONS = 600_000;

/**
 * Deliberately cheap: it runs inside the 10 ms budget. Its job is not to be a work factor —
 * authKey is already high-entropy — but to stop a leaked database from containing values
 * that are directly replayable as credentials.
 */
export const SERVER_ITERATIONS = 1_000;

const KEY_BYTES = 32;
const SALT_DOMAIN = 'offbook.auth.v1';

/** Username identity, used for the unique index AND the salt. The two must never diverge. */
export function normalizeUsername(username: string): string {
  return username.normalize('NFKC').trim().toLowerCase();
}

/**
 * The copy is deliberate. The DOM lib and @cloudflare/workers-types disagree about whether
 * TextEncoder returns Uint8Array<ArrayBuffer> or Uint8Array<ArrayBufferLike>, and this file
 * has to compile clean under both. Re-wrapping pins the buffer type without a cast, and the
 * inputs here are a password and a username.
 */
function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(s));
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/**
 * Deterministic, so the client can derive it before authenticating. A salt-fetch endpoint
 * would leak which usernames exist. Domain-separated so a hash is not reusable elsewhere.
 */
export async function authKeySalt(username: string): Promise<Uint8Array<ArrayBuffer>> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    utf8(`${SALT_DOMAIN} ${normalizeUsername(username)}`),
  );
  return new Uint8Array(digest);
}

async function pbkdf2(
  password: Uint8Array<ArrayBuffer>,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const key = await crypto.subtle.importKey('raw', password, 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    key,
    KEY_BYTES * 8,
  );
  return new Uint8Array(bits);
}

/**
 * Browser side. The password never leaves the device — this is what goes over the wire.
 * Takes 300–800 ms on a phone, so the caller owes the user a spinner.
 */
export async function deriveAuthKey(username: string, password: string): Promise<string> {
  const salt = await authKeySalt(username);
  return toBase64(await pbkdf2(utf8(password), salt, CLIENT_ITERATIONS));
}

/** Server side. Cheap by design; see SERVER_ITERATIONS. */
export async function hashAuthKey(authKeyB64: string, serverSaltB64: string): Promise<string> {
  const derived = await pbkdf2(
    fromBase64(authKeyB64),
    fromBase64(serverSaltB64),
    SERVER_ITERATIONS,
  );
  return toBase64(derived);
}

export function randomSaltB64(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return toBase64(bytes);
}

/** Constant-time comparison. A length-independent early return here would leak the hash. */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
